import React, { useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import { getRolesByCategory, CATEGORY_ACCENT, CATEGORY_BG } from '../../config/roleMetadata';

/**
 * Collapsible "Roles explained" legend panel.
 *
 * Presentational only. Lists every role grouped by category with its icon,
 * color, label and a user-facing description so end users understand what each
 * role grants before assigning it.
 *
 * @param {object} props
 * @param {boolean} [props.defaultOpen=false] - Whether the panel starts expanded.
 */
export default function RolesLegend({ defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const grouped = getRolesByCategory();
  const totalRoles = grouped.reduce((sum, b) => sum + b.roles.length, 0);

  return (
    <div className="overflow-hidden rounded-2xl border border-indigo-100 bg-white/70 shadow-md ring-1 ring-indigo-100/50 backdrop-blur">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between bg-gradient-to-r from-indigo-50 via-violet-50 to-sky-50 px-5 py-4 text-left transition-colors hover:from-indigo-100 hover:to-sky-100"
      >
        <span className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 shadow-md">
            <InformationCircleIcon className="h-6 w-6 text-white" />
          </span>
          <span>
            <span className="flex items-center gap-2 text-sm font-bold text-gray-900">
              Roles explained
              <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                {totalRoles} roles
              </span>
            </span>
            <span className="block text-xs text-gray-500">
              What each role can do across a project
            </span>
          </span>
        </span>
        {open ? (
          <ChevronUpIcon className="h-5 w-5 text-indigo-500" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-indigo-500" />
        )}
      </button>

      {open && (
        <div className="border-t border-indigo-100 bg-white px-5 py-5">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {grouped.map((bucket) => (
              <div key={bucket.category}>
                <div className="mb-3 flex items-center gap-2">
                  <span
                    className={`h-2.5 w-2.5 rounded-full bg-gradient-to-r ${
                      CATEGORY_BG[bucket.category] || 'from-gray-400 to-gray-500'
                    }`}
                  />
                  <h4
                    className={`text-xs font-bold uppercase tracking-wider ${
                      CATEGORY_ACCENT[bucket.category] || 'text-gray-600'
                    }`}
                  >
                    {bucket.category}
                  </h4>
                  <span className="h-px flex-1 bg-gradient-to-r from-gray-200 to-transparent" />
                </div>
                <ul className="space-y-2.5">
                  {bucket.roles.map((role) => {
                    const { Icon } = role;
                    return (
                      <li
                        key={role.name}
                        className="group flex items-start gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md"
                      >
                        <span
                          className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${role.color.iconBg} ring-1 ${role.color.ring}`}
                        >
                          <Icon className={`h-4 w-4 ${role.color.iconText}`} />
                        </span>
                        <div className="min-w-0">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${role.color.badge}`}
                          >
                            {role.label}
                          </span>
                          <p className="mt-1.5 text-xs leading-relaxed text-gray-600">
                            {role.description}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
